import React, { useMemo, useState } from 'react';
import { Button } from '../../components/ui';
import type { ProjectPlan } from '../../../../shared/types/plans';
import { PLANS } from '../../../../shared/types/plans';
import type { OnboardingAnswers } from '../../../../shared/types/accounts';
import { recommendPlan } from '../../../../shared/lib/planRecommendation';
import { floorPlanAtTeam } from './state';
import { StackFields } from './StackFields';
import { ScaleFields } from './ScaleFields';
import { NeedsFields } from './NeedsFields';
import { PlanFields } from './PlanFields';

interface Props {
  answers: OnboardingAnswers;
  onAnswersChange: (answers: OnboardingAnswers) => void;
  chosenPlan: ProjectPlan;
  onChoosePlan: (plan: ProjectPlan) => void;
  onNext: () => void;
  onBack: () => void;
  loading?: boolean;
  error?: string;
}

/**
 * UX-08 step 2 — "Your codebase": stack + scale + needs on one screen
 * (previously 3 separate steps), with a compact plan recommendation and
 * an optional "Change plan" disclosure that reveals the full picker
 * (previously its own 4th step). Continuing here creates the account
 * and first project, exactly like the old Plan step's Continue did.
 */
export const StepCodebase: React.FC<Props> = ({
  answers,
  onAnswersChange,
  chosenPlan,
  onChoosePlan,
  onNext,
  onBack,
  loading,
  error,
}) => {
  const [planOpen, setPlanOpen] = useState(false);
  const rec = useMemo(() => recommendPlan(answers), [answers]);
  const recommendedPlan = floorPlanAtTeam(rec.plan);
  const reasonText = rec.reasons.length > 0 ? rec.reasons.map((r) => r.answer).join(', ') : null;

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Tech stack</h3>
        <StackFields
          stacks={answers.stacks}
          onChange={(stacks) => onAnswersChange({ ...answers, stacks })}
        />
      </section>

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Scale</h3>
        <ScaleFields
          repoCount={answers.repoCount}
          orgCount={answers.orgCount}
          projectCount={answers.projectCount}
          onChange={(field, value) => onAnswersChange({ ...answers, [field]: value })}
        />
      </section>

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Needs</h3>
        <NeedsFields
          needs={answers.needs}
          onChange={(needs) => onAnswersChange({ ...answers, needs })}
        />
      </section>

      <div className="border-t border-slate-200 pt-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-800">
            <span className="font-semibold">Recommended: {PLANS[recommendedPlan].label}</span>
            {reasonText && <span className="text-slate-500"> — fits {reasonText}</span>}
          </p>
          <button
            type="button"
            onClick={() => setPlanOpen((o) => !o)}
            data-testid="onboard-change-plan-toggle"
            aria-expanded={planOpen}
            className="shrink-0 text-xs text-primary-500 hover:text-primary-600 hover:underline whitespace-nowrap"
          >
            {planOpen ? 'Hide plans ▴' : 'Change plan ▾'}
          </button>
        </div>
        {planOpen && (
          <div data-testid="onboard-plan-picker">
            <PlanFields answers={answers} chosenPlan={chosenPlan} onChoose={onChoosePlan} />
          </div>
        )}
        <p className="text-xs text-slate-500">API keys are optional — add them later in project settings.</p>
      </div>

      {error && <p className="text-xs text-danger-700">{error}</p>}

      <div className="flex gap-3 pt-2">
        <Button variant="ghost" size="md" onClick={onBack} className="flex-1">Back</Button>
        <Button variant="primary" size="md" onClick={onNext} loading={loading} className="flex-1">
          {loading ? 'Creating account…' : 'Continue'}
        </Button>
      </div>
    </div>
  );
};
